import React, { useState, useRef, useEffect, useLayoutEffect, useCallback, ChangeEvent } from 'react';
import { Paperclip, Send, Camera, Image as ImageIcon, X, Square, FileText, MessageSquare } from 'lucide-react';
import AudioRecorder from './AudioRecorder';
import ComposerPopover from './ComposerPopover';
import ReasoningSelector from './ReasoningSelector';
import ChatMentionComposer from './ChatMentionComposer';
import { excludeCurrentChatMention, trimMentionDraft, startChatMention } from './chatMentionEditing';
import { validateChatMentions, type ChatMention, type MentionableChat } from '../shared/chatMentions';
import type { ReasoningPreference } from '../types';
import type { SubscriptionTier } from '../config/subscriptionPlans';
import { getUnsupportedDiligenceFormatMessage } from '../utils/diligenceUploadErrors';
import { canAutoFocusComposer } from '../utils/composerFocus';
import { emptyComposerDraft, type ComposerDraft, type ComposerDraftBinding } from '../utils/composerDrafts';

interface MessageInputProps {
  onSendMessage: (text: string | undefined, images: File[], audio: Blob | null, document?: File | null, chatMentions?: ChatMention[]) => void;
  mentionableChats?: MentionableChat[];
  currentChatId?: string | null;
  draftBinding?: ComposerDraftBinding;
  onOpenChat?: (id: string) => void;
  isLoading: boolean;
  isSendingDisabled?: boolean;
  deferClearUntilAccepted?: boolean;
  draftRevision?: number;
  onStopGeneration?: () => void;
  autoFocus?: boolean;
  reasoningPreference: ReasoningPreference;
  userTier: SubscriptionTier;
  onReasoningChange: (value: ReasoningPreference) => void;
}

const MAX_DOCUMENT_BYTES = 20 * 1024 * 1024;
const MAX_IMAGE_BYTES = 20 * 1024 * 1024;
const SUPPORTED_IMAGE_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp']);
const NO_CHATS: MentionableChat[] = [];

const MessageInput: React.FC<MessageInputProps> = ({ onSendMessage, isLoading, isSendingDisabled = false, deferClearUntilAccepted = false, draftRevision = 0, onStopGeneration, autoFocus = false, reasoningPreference, userTier, onReasoningChange, mentionableChats = NO_CHATS, currentChatId, onOpenChat, draftBinding }) => {
  const [draft, setDraft] = useState<ComposerDraft>(() => draftBinding?.read() ?? emptyComposerDraft());
  const { text, chatMentions, images, document: selectedDocument } = draft;
  const draftRef = useRef(draft);
  const mountedRef = useRef(true);
  const [mentionRequest, setMentionRequest] = useState<{ sequence: number; caret: number }>();
  const [selectedImages, setSelectedImages] = useState<Array<{ file: File; previewUrl: string }>>([]);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [activeMenu, setActiveMenu] = useState<'attachments' | 'reasoning' | null>(null);
  const showAttachMenu = activeMenu === 'attachments';
  // AudioRecorder can finish asynchronously with an older callback.
  const canSendRef = useRef(false);
  canSendRef.current = !isLoading && !isSendingDisabled;

  // Reference for Gallery upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Reference for Camera capture
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const documentInputRef = useRef<HTMLInputElement>(null);

  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wasRecordingRef = useRef(false);
  const ignoreAttachUntilRef = useRef(0);
  const setShowAttachMenu = useCallback((open: boolean) => {
    if (open && Date.now() < ignoreAttachUntilRef.current) return;
    setActiveMenu(open ? 'attachments' : null);
  }, []);
  const setShowReasoningMenu = useCallback((open: boolean) => setActiveMenu(open ? 'reasoning' : null), []);

  const isCurrentComposer = () => mountedRef.current && (!draftBinding || draftBinding.isActive());
  const updateDraft = (patch: Partial<ComposerDraft>) => {
    if (!isCurrentComposer()) return;
    const next = { ...draftRef.current, ...patch };
    draftRef.current = next;
    draftBinding?.write(next);
    setDraft(next);
  };

  useLayoutEffect(() => {
    if (!draftBinding) return;
    const next = draftBinding.read();
    draftRef.current = next;
    setDraft(next);
  }, [draftBinding, draftRevision]);

  useLayoutEffect(() => {
    mountedRef.current = true;
    const release = draftBinding?.activate();
    return () => {
      mountedRef.current = false;
      release?.();
    };
  }, [draftBinding]);

  useLayoutEffect(() => {
    const previews = images.map((file) => ({ file, previewUrl: URL.createObjectURL(file) }));
    setSelectedImages(previews);
    return () => previews.forEach(({ previewUrl }) => URL.revokeObjectURL(previewUrl));
  }, [images]);

  useLayoutEffect(() => {
    if (!autoFocus || !canAutoFocusComposer()) return;
    const input = textareaRef.current;
    input?.focus({ preventScroll: true });
    const focusFrame = window.requestAnimationFrame(() => {
      if (canAutoFocusComposer() && document.activeElement !== input) input?.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(focusFrame);
  }, [autoFocus]);

  useEffect(() => {
    if (wasRecordingRef.current && !isRecording) {
      ignoreAttachUntilRef.current = Date.now() + 400;
    }

    wasRecordingRef.current = isRecording;
    if (isRecording) setActiveMenu(null);
  }, [isRecording]);

  const removeImage = (previewUrl: string) => {
    const removed = selectedImages.find((image) => image.previewUrl === previewUrl)?.file;
    updateDraft({ images: images.filter((file) => file !== removed) });
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const maxImages = 5;
    if (images.length + files.length > maxImages) {
      setAttachmentError('Puedes adjuntar hasta 5 imágenes por mensaje.');
      setShowAttachMenu(false);
      return;
    }
    if (files.some((file) => !SUPPORTED_IMAGE_TYPES.has(file.type) || file.size === 0 || file.size >= MAX_IMAGE_BYTES)) {
      setAttachmentError('Usa imágenes PNG, JPEG o WebP de menos de 20 MiB cada una.');
      setShowAttachMenu(false);
      return;
    }
    updateDraft({ images: [...images, ...files] });
    setAttachmentError(null);
    setShowAttachMenu(false);
  };

  const handleDocumentSelect = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.target.files?.[0] || null;
    if (!next) return;
    const unsupportedFormatMessage = getUnsupportedDiligenceFormatMessage(next.name);
    if (unsupportedFormatMessage) {
      setAttachmentError(unsupportedFormatMessage);
      event.target.value = '';
      return;
    }
    if (next.size > MAX_DOCUMENT_BYTES) {
      setAttachmentError('El documento no puede superar 20 MiB.');
      event.target.value = '';
      return;
    }
    updateDraft({ document: next });
    setAttachmentError(null);
    setShowAttachMenu(false);
    event.target.value = '';
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
    setShowAttachMenu(false);
  };

  const triggerCamera = () => {
    cameraInputRef.current?.click();
    setShowAttachMenu(false);
  };

  const openChatMentions = () => {
    const input = textareaRef.current;
    const draft = startChatMention(text, chatMentions, {
      start: input?.selectionStart ?? text.length, end: input?.selectionEnd ?? text.length
    });
    setShowAttachMenu(false);
    updateDraft({ text: draft.text, chatMentions: draft.mentions });
    setMentionRequest(previous => ({ sequence: (previous?.sequence ?? 0) + 1, caret: draft.caret }));
    // Keep focus within this user gesture so mobile keyboards can reopen.
    input?.focus({ preventScroll: true });
  };

  const handleSend = () => {
    if ((!text.trim() && !images.length && !selectedDocument) || !canSendRef.current || !isCurrentComposer()) return;

    const draft = trimMentionDraft(text, excludeCurrentChatMention(chatMentions, currentChatId));
    if (!validateChatMentions(draft.mentions, draft.text)) {
      setAttachmentError('Revisa las menciones de chats antes de enviar el mensaje.');
      return;
    }
    // Reset state
    if (!deferClearUntilAccepted) updateDraft(emptyComposerDraft());
    onSendMessage(draft.text || undefined, images, null, selectedDocument, draft.mentions);
    setAttachmentError(null);
    setShowAttachMenu(false);
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleSendAudio = (audioBlob: Blob) => {
    if (!canSendRef.current || !isCurrentComposer()) return;
    onSendMessage(undefined, [], audioBlob);
  };

  // Determine if we show the Send button or the Mic button
  // Show Send if there is text OR an image. Show Mic otherwise.
  const showSendButton = text.trim().length > 0 || images.length > 0 || selectedDocument !== null;

  return (
    <div className="w-full bg-canvas px-2 pt-2 pb-[calc(0.5rem+env(safe-area-inset-bottom,0px))] sm:px-4 sm:pb-[calc(1rem+env(safe-area-inset-bottom,0px))]">
      <div className="relative mx-auto w-full max-w-4xl rounded-3xl border border-line/50 bg-panel p-2 shadow-xl shadow-black/30 focus-within:border-line sm:p-3">

      {/* Image Preview Area */}
      {attachmentError && !isRecording && (
        <div
          className="mx-2 mb-2 rounded-lg border border-warning bg-warning-soft px-3 py-2 text-sm font-medium text-warning"
          role="status"
          aria-live="polite"
        >
          {attachmentError}
        </div>
      )}

      {selectedImages.length > 0 && !isRecording && (
        <div className="mx-2 mb-2 flex max-w-full gap-2 overflow-x-auto pt-2" aria-label="Imágenes adjuntas">
          {selectedImages.map(({ previewUrl }, index) => <div key={previewUrl} className="relative shrink-0">
            <img src={previewUrl} alt={`Imagen ${index + 1} para enviar`} className="h-16 w-16 rounded-lg border border-line object-cover" />
            <button type="button" onClick={() => removeImage(previewUrl)} className="absolute -right-1 -top-1 flex h-6 w-6 items-center justify-center rounded-full bg-action text-white shadow-sm" aria-label={`Quitar imagen ${index + 1}`}><X size={14} /></button>
          </div>)}
        </div>
      )}

      {selectedDocument && !isRecording && (
        <div className="mx-2 mb-2 inline-flex max-w-[calc(100%-1rem)] items-center gap-2 rounded-xl border border-warning bg-warning-soft px-3 py-2 text-sm font-semibold text-warning">
          <FileText size={17} className="shrink-0" />
          <span className="truncate">{selectedDocument.name}</span>
          <button type="button" onClick={() => updateDraft({ document: null })} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full hover:bg-warning-soft" aria-label="Quitar documento"><X size={14} /></button>
        </div>
      )}

      <ChatMentionComposer value={text} mentions={chatMentions} mentionRequest={mentionRequest}
        onChange={(nextText, nextMentions) => updateDraft({ text: nextText, chatMentions: nextMentions })}
        onSend={handleSend} textareaRef={textareaRef} chats={mentionableChats}
        currentChatId={currentChatId} onOpenChat={onOpenChat} isRecording={isRecording} />
      <div className="relative mt-1 flex min-h-12 w-full items-center justify-between gap-2">
        {/* Hidden Inputs */}
        <input
          type="file"
          ref={fileInputRef}
          accept="image/png,image/jpeg,image/webp"
          multiple
          className="hidden"
          onChange={handleFileSelect}
        />
        <input
          type="file"
          ref={cameraInputRef}
          accept="image/png,image/jpeg,image/webp"
          capture="environment"
          className="hidden"
          onChange={handleFileSelect}
        />
        <input type="file" ref={documentInputRef} accept=".pdf,.doc,.docx,.xls,.xlsx,.ods" className="hidden" onChange={handleDocumentSelect} />

        <div className={isRecording ? 'invisible' : ''}>
          <ComposerPopover label="Adjuntar archivo" open={showAttachMenu} onOpenChange={setShowAttachMenu}
            disabled={isRecording} trigger={<span className={`transition-transform duration-200 ${showAttachMenu ? 'rotate-45' : ''}`}><Paperclip size={22} aria-hidden="true" /></span>}>
                <button
                  onClick={triggerCamera}
                  role="menuitem"
                  tabIndex={-1}
                className="flex items-center gap-3 px-3 py-2.5 min-h-11 hover:bg-juris-soft rounded-lg text-ink transition-colors text-sm font-medium w-full text-left"
                >
                  <div className="bg-juris-soft p-1.5 rounded-full text-juris">
                    <Camera size={18} />
                  </div>
                  Cámara
                </button>
                <button
                  onClick={triggerFileSelect}
                  role="menuitem"
                  tabIndex={-1}
                className="flex items-center gap-3 px-3 py-2.5 min-h-11 hover:bg-juris-soft rounded-lg text-ink transition-colors text-sm font-medium w-full text-left"
                >
                  <div className="bg-info-soft p-1.5 rounded-full text-info">
                    <ImageIcon size={18} />
                  </div>
                  Galería
                </button>
                <button type="button" onClick={() => { documentInputRef.current?.click(); setShowAttachMenu(false); }} role="menuitem" tabIndex={-1} className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-ink hover:bg-warning-soft"><span className="rounded-full bg-warning-soft p-1.5 text-warning"><FileText size={18} /></span>Documentos</button>
                <button type="button" onClick={(event) => { event.stopPropagation(); openChatMentions(); }} role="menuitem" tabIndex={-1}
                  className="flex min-h-11 w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm font-medium text-ink hover:bg-juris-soft">
                  <span className="rounded-full bg-juris-soft p-1.5 text-juris"><MessageSquare size={18} aria-hidden="true" /></span>Chats
                </button>
          </ComposerPopover>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          <div className={isRecording ? 'invisible' : ''}>
            <ReasoningSelector value={reasoningPreference} userTier={userTier} onChange={onReasoningChange}
              open={activeMenu === 'reasoning'} onOpenChange={setShowReasoningMenu}
              disabled={isRecording || isLoading || isSendingDisabled} />
          </div>
        {/* Botón de Acción (Micrófono o Enviar) */}
        <div className="flex h-12 w-12 shrink-0 items-center justify-center">
          {isLoading ? (
            <button
              onClick={onStopGeneration}
              title="Detener respuesta"
              aria-label="Detener respuesta"
              className="w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-md transform motion-micro-scale bg-danger-soft hover:bg-danger-soft text-danger cursor-pointer"
            >
              <Square size={18} className="fill-current" />
            </button>
          ) : showSendButton ? (
            <button
              onClick={handleSend}
              disabled={isLoading || isSendingDisabled}
              aria-label="Enviar mensaje"
              className={`w-11 h-11 rounded-full flex items-center justify-center transition-all shadow-md transform motion-micro-scale ${isLoading || isSendingDisabled
                ? 'bg-line cursor-not-allowed'
                : 'bg-action hover:bg-action-hover text-white'
                }`}
            >
              {/* Centered icon without extra margins */}
              <Send size={20} className="translate-x-[-1px] translate-y-[1px]" />
            </button>
          ) : (
            <AudioRecorder
              onSendAudio={handleSendAudio}
              onRecordingStateChange={setIsRecording}
              disabled={isLoading || isSendingDisabled}
            />
          )}
        </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default MessageInput;
